import { matchElapsedSeconds } from "../src/lib/results/match-state";

/**
 * Julkisten sivujen reaaliaikapäivitys (progressiivinen parannus): kuuntelee turnauksen SSE-kanavaa,
 * päivittää tuloksen ja kellon elementteihin data-match-id:n perusteella ja lataa sivun <main>-sisällön
 * uudelleen, kun aikataulu tai sarjataulukko muuttuu. Käynnissä olevat kellot tikittävät paikallisesti.
 * Sisällön vaihdon jälkeen lähetetään `kt:main-replaced`, jolloin public.js kiinnittää sivun toiminnot
 * (suodattimet, aikataulun kopiointi) uusiin elementteihin.
 *
 * Hallinnan turnaustoimisto käyttää samaa kanavaa: `[data-tournament]`-elementin `data-live-reload="match"`
 * lataa sisällön myös jokaisen ottelutapahtuman jälkeen (tilamerkit, käynnissä olevat, hälytykset) ja
 * `data-live-refresh="30"` päivittää sen määrävälein (aikaan perustuvat hälytykset).
 */

interface MatchClock {
  periodSeconds: number;
  period: number;
  running: boolean;
  periodStartedAt: string | null;
  accumulatedSeconds: number;
}
interface MatchLiveState {
  matchId: string;
  state: string;
  homeScore: number;
  awayScore: number;
  clock: MatchClock;
  updatedAt: string;
  periodMinutes?: number;
}
type LiveEvent =
  | { type: "match"; match: MatchLiveState }
  | { type: "schedule" }
  | { type: "standings" }
  | { type: "announcement" }
  | { type: "ping" };

const RELOAD_DEBOUNCE_MS = 1500;
const holder = document.querySelector<HTMLElement>("[data-tournament]");
if (holder?.dataset.tournament) {
  start(holder.dataset.tournament, {
    reloadOnMatch: holder.dataset.liveReload === "match",
    refreshSeconds: Number(holder.dataset.liveRefresh ?? 0) || 0,
  });
}

function start(tournamentId: string, opts: { reloadOnMatch: boolean; refreshSeconds: number }) {
  const sseUrl = `/api/v1/live/${encodeURIComponent(tournamentId)}/sse`;
  let reloadTimer: number | undefined;
  let es: EventSource | null = null;

  const fmtClock = (total: number): string => {
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  /** Kokonaispeliaika: aiemmat jaksot täysimittaisina + nykyisen jakson aika (2 × 12 min, jakso 2 1:00 → 13:00). */
  function elapsedText(el: HTMLElement, periodSeconds: number): string {
    const period = Number(el.dataset.period ?? 1);
    const minutes = Number(el.dataset.periodMinutes ?? 0);
    return fmtClock(matchElapsedSeconds(period, periodSeconds, Number.isNaN(minutes) ? 0 : minutes));
  }

  /** Käynnissä kokonaispeliaika, pysäytettynä "Tauko" (data-label-paused). */
  function clockText(el: HTMLElement, periodSeconds: number, running: boolean): string {
    return running ? elapsedText(el, periodSeconds) : (el.dataset.labelPaused ?? "");
  }

  function applyMatch(live: MatchLiveState) {
    const rows = document.querySelectorAll<HTMLElement>(`[data-match-id="${CSS.escape(live.matchId)}"]`);
    for (const row of rows) {
      row.dataset.state = live.state;
      const score = row.querySelector<HTMLElement>("[data-score]");
      if (score) {
        const text = live.state === "scheduled" ? "–" : `${live.homeScore}–${live.awayScore}`;
        if (score.textContent !== text) score.textContent = text;
      }
      const clock = row.querySelector<HTMLElement>("[data-clock]");
      if (clock) {
        clock.dataset.running = live.state === "live" && live.clock.running ? "1" : "0";
        clock.dataset.period = String(live.clock.period);
        clock.dataset.periodStarted = live.clock.periodStartedAt ?? "";
        clock.dataset.accumulated = String(live.clock.accumulatedSeconds);
        clock.dataset.periodSeconds = String(live.clock.periodSeconds);
        if (live.periodMinutes !== undefined) clock.dataset.periodMinutes = String(live.periodMinutes);
        clock.classList.toggle("live", live.state === "live");
        if (live.state === "live") {
          clock.textContent = clockText(clock, live.clock.periodSeconds, live.clock.running);
        } else if (live.state === "finished" || live.state === "walkover") {
          clock.textContent =
            clock.dataset[`label${live.state === "finished" ? "Finished" : "Walkover"}`] ?? clock.textContent;
        }
      }
      const badge = row.querySelector<HTMLElement>("[data-live-badge]");
      if (badge) badge.hidden = live.state !== "live";
    }
    // Ottelu, jota sivulla ei ole (esim. alkoi juuri): ladataan osio, jotta "Suorana" täydentyy.
    if (rows.length === 0 && (live.state === "live" || live.state === "finished")) scheduleReload();
  }

  function scheduleReload() {
    window.clearTimeout(reloadTimer);
    reloadTimer = window.setTimeout(reloadMain, RELOAD_DEBOUNCE_MS);
  }

  async function reloadMain() {
    try {
      const res = await fetch(window.location.href, {
        headers: { Accept: "text/html" },
        cache: "no-store",
        credentials: "same-origin",
      });
      if (!res.ok) return;
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const fresh = doc.querySelector("main#main");
      const current = document.querySelector("main#main");
      if (!fresh || !current) return;
      // Kirjoittaminen (esim. joukkuehaku) jatkuu keskeytyksettä: arvo, kohdistus ja kursori siirtyvät uuteen kenttään.
      const active = document.activeElement;
      const typing =
        active instanceof HTMLInputElement && active.id && current.contains(active) ? active : null;
      current.replaceWith(fresh);
      if (typing) {
        const input = document.getElementById(typing.id);
        if (input instanceof HTMLInputElement) {
          input.value = typing.value;
          input.focus();
          input.setSelectionRange(typing.selectionStart, typing.selectionEnd);
        }
      }
      document.dispatchEvent(new CustomEvent("kt:main-replaced"));
    } catch {
      // yritetään seuraavalla tapahtumalla
    }
  }

  function tick() {
    const now = Date.now();
    for (const c of document.querySelectorAll<HTMLElement>("[data-clock][data-running='1']")) {
      const started = Date.parse(c.dataset.periodStarted ?? "");
      if (Number.isNaN(started)) continue;
      const secs = Number(c.dataset.accumulated ?? 0) + Math.max(0, Math.floor((now - started) / 1000));
      c.textContent = clockText(c, secs, true);
    }
  }

  function connect() {
    if (typeof EventSource === "undefined") return;
    es = new EventSource(sseUrl);
    // DO lähettää nimetyt tapahtumat ("event: match" jne.), joita onmessage ei vastaanota.
    const handle = (ev: Event) => {
      let data: LiveEvent | null = null;
      try {
        data = JSON.parse((ev as MessageEvent<string>).data) as LiveEvent;
      } catch {
        return;
      }
      if (!data) return;
      switch (data.type) {
        case "match":
          applyMatch(data.match);
          if (opts.reloadOnMatch) scheduleReload();
          break;
        case "schedule":
        case "standings":
          scheduleReload();
          break;
        default:
          break;
      }
    };
    for (const type of ["match", "schedule", "standings", "message"]) es.addEventListener(type, handle);
    es.onerror = () => {
      es?.close();
      window.setTimeout(connect, 5000);
    };
  }

  window.setInterval(tick, 1000);
  if (opts.refreshSeconds > 0) {
    window.setInterval(() => {
      if (document.visibilityState === "visible") scheduleReload();
    }, opts.refreshSeconds * 1000);
  }
  connect();
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") scheduleReload();
  });
}
